/**
 * The reporting-governance route table (design C12 §5.5).
 *
 * `web/src/router/index.ts` is P11's file, never this packet's, so the table is
 * declared here and imported there — the shape every module in this app uses.
 *
 * The metric detail is a child path rather than a drawer because a certification
 * is something people link each other to: "see the basis on `ar.dso`" has to
 * survive being pasted into an email.
 */

import type { RouteRecordRaw } from 'vue-router'

import { REPORTING_PERMISSIONS } from '@/modules/reporting/permissions'

export const REPORTING_ROUTES: RouteRecordRaw[] = [
  {
    path: '/reporting/metrics',
    name: 'reporting-metrics',
    component: () => import('@/modules/reporting/MetricDictionaryView.vue'),
    meta: {
      titleKey: 'reporting.routeTitles.dictionary',
      permission: REPORTING_PERMISSIONS.metricRead,
    },
  },
  {
    path: '/reporting/metrics/:code',
    name: 'reporting-metric',
    component: () => import('@/modules/reporting/MetricDetailView.vue'),
    meta: {
      titleKey: 'reporting.routeTitles.metric',
      permission: REPORTING_PERMISSIONS.metricRead,
    },
  },
  {
    path: '/reporting/daily-operating',
    name: 'reporting-daily-operating',
    component: () => import('@/modules/reporting/DailyOperatingView.vue'),
    meta: {
      titleKey: 'reporting.routeTitles.dailyOperating',
      permission: REPORTING_PERMISSIONS.dailyOpsRead,
    },
  },
  {
    path: '/reporting/daily-operating-board',
    name: 'reporting-daily-operating-board',
    component: () => import('@/modules/reporting/DailyOperatingBoardView.vue'),
    meta: {
      titleKey: 'reporting.routeTitles.board',
      permission: REPORTING_PERMISSIONS.dailyOpsRead,
    },
  },
  {
    path: '/reporting/exception-views',
    name: 'reporting-exception-views',
    component: () => import('@/modules/reporting/ExceptionViewsView.vue'),
    meta: {
      titleKey: 'reporting.routeTitles.exceptionViews',
      permission: REPORTING_PERMISSIONS.exceptionViewRead,
    },
  },

  /* §8.17's report centre: the role dashboards, the rows behind one of their
     cards, and the one standing object a reader keeps — a saved filter set.
     Scheduled delivery has no screen: the FSD carries no scheduled-report
     requirement, so C17 removed it from the UI (the server routes remain). */
  {
    path: '/reporting/dashboards',
    name: 'reporting-dashboards',
    component: () => import('@/modules/reporting/DashboardsView.vue'),
    meta: {
      titleKey: 'reporting.routeTitles.dashboards',
      permission: REPORTING_PERMISSIONS.dashboardRead,
    },
  },
  {
    // The drill-through is a path, not a drawer, for the same reason the metric
    // detail is: "here is what makes up our DSO" has to survive being pasted
    // into an email, and an accountant checking a figure wants the URL.
    path: '/reporting/kpis/:key',
    name: 'reporting-kpi-drill',
    component: () => import('@/modules/reporting/KpiDrillView.vue'),
    meta: {
      titleKey: 'reporting.routeTitles.drill',
      permission: REPORTING_PERMISSIONS.dashboardRead,
    },
  },
  {
    path: '/reporting/saved-filters',
    name: 'reporting-saved-filters',
    component: () => import('@/modules/reporting/SavedFiltersView.vue'),
    meta: {
      titleKey: 'reporting.routeTitles.savedFilters',
      permission: REPORTING_PERMISSIONS.filterManage,
    },
  },
]

/* ------------------------------------------------------------ own screens */

export const REPORTING_PATHS = {
  dashboards: '/reporting/dashboards',
  dashboard: (name: string) => `/reporting/dashboards?dashboard=${encodeURIComponent(name)}`,
  kpiDrill: (key: string) => `/reporting/kpis/${encodeURIComponent(key)}`,
  savedFilters: '/reporting/saved-filters',
  metrics: '/reporting/metrics',
  dailyOperating: '/reporting/daily-operating',
  board: '/reporting/daily-operating-board',
  exceptionViews: '/reporting/exception-views',
} as const

/**
 * Where a `reportKey` points.
 *
 * A saved filter set carries a `report_key`, and the server
 * accepts exactly two shapes: `dashboard:<name>` and `kpi:<key>`
 * (`internal/reporting/export.go`'s `splitReportKey`). Anything else is a key
 * this build does not know how to open, and the honest answer is an empty
 * string — the caller renders the raw key as text rather than a link that would
 * land on a 404.
 */
export function reportKeyRoute(reportKey: string): string {
  const separator = reportKey.indexOf(':')
  if (separator <= 0) return ''
  const kind = reportKey.slice(0, separator)
  const name = reportKey.slice(separator + 1)
  if (name === '') return ''
  if (kind === 'dashboard') return REPORTING_PATHS.dashboard(name)
  if (kind === 'kpi') return REPORTING_PATHS.kpiDrill(name)
  return ''
}
